/**
 * Translate between Radix's single-select value and Base UI's array value.
 *
 * Several Radix components — Accordion and ToggleGroup so far — discriminate on
 * `type="single" | "multiple"` and type the value as a bare string in one mode
 * and an array in the other. Base UI dropped the discriminator for a `multiple`
 * boolean and made the value an array in **both** modes.
 *
 * The two functions here are the whole difference, and they exist as shared code
 * rather than as a copy per component because of one rule that is easy to get
 * wrong in both directions: **Radix says "nothing is selected" with the empty
 * string, Base UI says it with an empty array.** Mapping `""` to `[""]` gives a
 * group that can never be cleared, and mapping `[]` back to `""` is what lets a
 * controlled caller see its own deselection.
 *
 * `undefined` passes through untouched in both directions, so an uncontrolled
 * component stays uncontrolled and Base UI's own defaults apply.
 */

/** Radix's `value` / `defaultValue` for single mode → Base UI's array. */
export function toArrayValue(value: string | undefined): string[] | undefined {
  if (value === undefined) {
    return undefined;
  }
  return value === "" ? [] : [value];
}

/** Base UI's `onValueChange` array → the bare string Radix's callers expect. */
export function fromArrayValue(value: readonly string[]): string {
  return value[0] ?? "";
}
